import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { 
  Car, LayoutDashboard, FileText, LogOut, Users, Building, Monitor, Store, 
  BarChart3, Plus, Search, DollarSign, Briefcase, CreditCard, Receipt,
  Send, Check, Clock, AlertCircle, Download
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBilling() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [selectedAdv, setSelectedAdv] = useState(null);
  const [formData, setFormData] = useState({
    advertiser_id: "",
    period_start: "",
    period_end: "",
    due_days: 30,
    line_items: [{ description: "", quantity: 1, unit_price: 0 }]
  });
  const [paymentData, setPaymentData] = useState({
    advertiser_id: "",
    amount: "",
    description: ""
  });

  useEffect(() => {
    // Check for Stripe return
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchInvoices();
      fetchAdvertisers();
      fetchSummary();
    }
  }, [user, statusFilter]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      navigate("/admin/login");
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/billing/checkout/status/${sessionId}`, { withCredentials: true });
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful!");
      }
    } catch (error) {
      console.error("Payment status check failed:", error);
    }
  };

  const fetchInvoices = async () => {
    try {
      let url = `${API}/billing/invoices?limit=100`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get(`${API}/advertisers`, { withCredentials: true });
      setAdvertisers(response.data);
    } catch {}
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/billing/summary`, { withCredentials: true });
      setSummary(response.data);
    } catch {}
  };

  const handleCreateInvoice = async () => {
    try {
      const payload = {
        ...formData,
        line_items: formData.line_items.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        }))
      };
      await axios.post(`${API}/billing/invoices`, payload, { withCredentials: true });
      toast.success("Invoice created");
      setIsCreateOpen(false);
      resetForm();
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    }
  };

  const resetForm = () => {
    setFormData({
      advertiser_id: "", period_start: "", period_end: "", due_days: 30,
      line_items: [{ description: "", quantity: 1, unit_price: 0 }]
    });
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/billing/invoices/${invoiceId}/send`, {}, { withCredentials: true });
      toast.success("Invoice sent");
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      toast.error("Failed to send invoice");
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.patch(`${API}/billing/invoices/${invoiceId}`, 
        { status: "paid", paid_date: new Date().toISOString().split('T')[0], payment_method: "manual" },
        { withCredentials: true }
      );
      toast.success("Invoice marked as paid");
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  };

  const handleStripePayment = async () => {
    try {
      const response = await axios.post(`${API}/billing/checkout/create`, {
        advertiser_id: paymentData.advertiser_id,
        amount: parseFloat(paymentData.amount),
        description: paymentData.description || "Account credit",
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment session");
    }
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: "", quantity: 1, unit_price: 0 }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index][field] = value;
    setFormData({ ...formData, line_items: newItems });
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/admin/login");
  };

  const statusColors = {
    draft: "bg-slate-500/20 text-slate-400",
    sent: "bg-blue-500/20 text-blue-400",
    paid: "bg-emerald-500/20 text-emerald-400",
    overdue: "bg-red-500/20 text-red-400",
    cancelled: "bg-slate-500/20 text-slate-400"
  };

  const calculateTotal = () => {
    const subtotal = formData.line_items.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0
    );
    const vat = subtotal * 0.20;
    return { subtotal, vat, total: subtotal + vat };
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-billing">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6 overflow-y-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>ParkWise</span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" /><span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <FileText className="w-5 h-5" /><span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" /><span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" /><span>Retailers</span>
          </Link>
          
          {/* Revenue Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Revenue</p>
            <Link to="/admin/advertisers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Briefcase className="w-5 h-5" /><span>Advertisers</span>
            </Link>
            <Link to="/admin/campaigns" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="w-5 h-5" /><span>Campaigns</span>
            </Link>
            <Link to="/admin/billing" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
              <DollarSign className="w-5 h-5" /><span>Billing</span>
            </Link>
            <Link to="/admin/revenue" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="w-5 h-5" /><span>Revenue Dashboard</span>
            </Link>
          </div>

          {/* Signage CMS Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Signage CMS</p>
            <Link to="/admin/screens" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Monitor className="w-5 h-5" /><span>Screens</span>
            </Link>
            <Link to="/admin/content" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <FileText className="w-5 h-5" /><span>Content</span>
            </Link>
            <Link to="/admin/playlists" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <LayoutDashboard className="w-5 h-5" /><span>Playlists</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Billing</h1>
            <p className="text-slate-400">Manage invoices and payments</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-emerald-500 text-emerald-400">
                  <CreditCard className="w-4 h-4 mr-2" />Stripe Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 text-white">
                <DialogHeader>
                  <DialogTitle>Create Stripe Payment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Advertiser</Label>
                    <Select value={paymentData.advertiser_id} onValueChange={(v) => setPaymentData({...paymentData, advertiser_id: v})}>
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue placeholder="Select advertiser" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {advertisers.map(adv => (
                          <SelectItem key={adv.advertiser_id} value={adv.advertiser_id}>{adv.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount (£)</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input 
                      value={paymentData.description}
                      onChange={(e) => setPaymentData({...paymentData, description: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      placeholder="Account credit"
                    />
                  </div>
                  <Button onClick={handleStripePayment} className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <CreditCard className="w-4 h-4 mr-2" />Proceed to Payment
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" />New Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Invoice</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Advertiser *</Label>
                    <Select value={formData.advertiser_id} onValueChange={(v) => setFormData({...formData, advertiser_id: v})}>
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue placeholder="Select advertiser" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {advertisers.map(adv => (
                          <SelectItem key={adv.advertiser_id} value={adv.advertiser_id}>{adv.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Period Start</Label>
                      <Input 
                        type="date"
                        value={formData.period_start}
                        onChange={(e) => setFormData({...formData, period_start: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div>
                      <Label>Period End</Label>
                      <Input 
                        type="date"
                        value={formData.period_end}
                        onChange={(e) => setFormData({...formData, period_end: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div>
                      <Label>Payment Terms</Label>
                      <Select value={String(formData.due_days)} onValueChange={(v) => setFormData({...formData, due_days: parseInt(v)})}>
                        <SelectTrigger className="bg-slate-800 border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          <SelectItem value="30">Net 30</SelectItem>
                          <SelectItem value="60">Net 60</SelectItem>
                          <SelectItem value="0">Due on Receipt</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="mb-2 block">Line Items</Label>
                    {formData.line_items.map((item, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 mb-2">
                        <Input 
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                          className="col-span-6 bg-slate-800 border-slate-700"
                        />
                        <Input 
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                          className="col-span-2 bg-slate-800 border-slate-700"
                        />
                        <Input 
                          type="number"
                          step="0.01"
                          placeholder="Price"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)}
                          className="col-span-3 bg-slate-800 border-slate-700"
                        />
                        <div className="col-span-1 flex items-center justify-end text-slate-400">
                          £{((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addLineItem} className="mt-2">
                      <Plus className="w-4 h-4 mr-1" />Add Line
                    </Button>
                  </div>
                  
                  <div className="border-t border-slate-700 pt-4 space-y-2">
                    <div className="flex justify-between text-slate-400">
                      <span>Subtotal</span>
                      <span>£{calculateTotal().subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-400">
                      <span>VAT (20%)</span>
                      <span>£{calculateTotal().vat.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-white font-semibold text-lg">
                      <span>Total</span>
                      <span>£{calculateTotal().total.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <Button onClick={handleCreateInvoice} className="w-full bg-blue-600 hover:bg-blue-700">
                    Create Invoice
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card 
              className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-blue-500/50 transition-colors"
              onClick={() => setStatusFilter("all")}
              data-testid="kpi-total-invoiced"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Total Invoiced</CardTitle>
                <Receipt className="w-5 h-5 text-blue-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">£{(summary.total_invoiced || 0).toFixed(2)}</div>
              </CardContent>
            </Card>
            <Card 
              className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-emerald-500/50 transition-colors"
              onClick={() => setStatusFilter("paid")}
              data-testid="kpi-total-paid"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Total Paid</CardTitle>
                <Check className="w-5 h-5 text-emerald-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">£{(summary.total_paid || 0).toFixed(2)}</div>
              </CardContent>
            </Card>
            <Card 
              className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-amber-500/50 transition-colors"
              onClick={() => setStatusFilter("sent")}
              data-testid="kpi-outstanding"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Outstanding</CardTitle>
                <Clock className="w-5 h-5 text-amber-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">£{(summary.total_outstanding || 0).toFixed(2)}</div>
              </CardContent>
            </Card>
            <Card 
              className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-red-500/50 transition-colors"
              onClick={() => setStatusFilter("overdue")}
              data-testid="kpi-overdue"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Overdue</CardTitle>
                <AlertCircle className="w-5 h-5 text-red-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">£{(summary.total_overdue || 0).toFixed(2)}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Invoices List */}
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <Card key={invoice.invoice_id} className="bg-slate-800/50 border-slate-700" data-testid={`invoice-${invoice.invoice_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{invoice.invoice_number}</h3>
                      <Badge className={statusColors[invoice.status]}>{invoice.status}</Badge>
                    </div>
                    <p className="text-sm text-slate-400 mb-2">{invoice.advertiser_name}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="text-slate-400">
                        <span className="block text-xs text-slate-500">Period</span>
                        {invoice.period_start} - {invoice.period_end}
                      </div>
                      <div className="text-slate-400">
                        <span className="block text-xs text-slate-500">Due Date</span>
                        {invoice.due_date}
                      </div>
                      <div className="text-slate-400">
                        <span className="block text-xs text-slate-500">Subtotal</span>
                        £{(invoice.subtotal || 0).toFixed(2)}
                      </div>
                      <div className="text-white font-semibold">
                        <span className="block text-xs text-slate-500">Total</span>
                        £{(invoice.total || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {invoice.status === "draft" && (
                      <Button 
                        size="sm" 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleSendInvoice(invoice.invoice_id)}
                      >
                        <Send className="w-4 h-4 mr-1" />Send
                      </Button>
                    )}
                    {invoice.status === "sent" && (
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleMarkPaid(invoice.invoice_id)}
                      >
                        <Check className="w-4 h-4 mr-1" />Mark Paid
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {invoices.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-400">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No invoices found</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
